/**
 * Indirection between the transport layer and the auth feature.
 *
 * The API client needs tokens, and refreshing a token needs the API client — importing
 * the auth feature directly from `shared/` would both invert the dependency direction and
 * create a cycle. Instead the auth feature registers an implementation during startup.
 */
export interface AuthTokenProvider {
  /** Current access token, or null when the user is not signed in. */
  getAccessToken: () => string | null;
  /** Uses IdentityServer's HttpOnly session cookie to obtain a new access token. */
  refreshAccessToken: () => Promise<string>;
  /** Called when refreshing fails and the session cannot be recovered. */
  onSessionExpired: () => void;
}

const anonymousProvider: AuthTokenProvider = {
  getAccessToken: () => null,
  refreshAccessToken: () => Promise.reject(new Error('No authentication provider registered.')),
  onSessionExpired: () => undefined,
};

let activeProvider: AuthTokenProvider = anonymousProvider;

export function registerAuthTokenProvider(provider: AuthTokenProvider): void {
  activeProvider = provider;
}

export function resetAuthTokenProvider(): void {
  activeProvider = anonymousProvider;
}

export function getAuthTokenProvider(): AuthTokenProvider {
  return activeProvider;
}
